import { useState, useRef, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { Upload, User, Package, X, Loader2, ImagePlus, Palette } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { compressImageToFile } from "@/lib/imageCompression";

interface VisualBrandModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VisualBrandModal({ open, onOpenChange }: VisualBrandModalProps) {
  const [mode, setMode] = useState<"person" | "product">("person");
  const [mainPhoto, setMainPhoto] = useState<File | null>(null);
  const [mainPhotoPreview, setMainPhotoPreview] = useState<string | null>(null);
  const [referencePhoto, setReferencePhoto] = useState<File | null>(null);
  const [referencePhotoPreview, setReferencePhotoPreview] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  
  const mainPhotoInputRef = useRef<HTMLInputElement>(null);
  const referencePhotoInputRef = useRef<HTMLInputElement>(null);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const handleMainPhotoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setMainPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleReferencePhotoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReferencePhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setReferencePhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const clearMainPhoto = () => {
    setMainPhoto(null);
    setMainPhotoPreview(null);
    if (mainPhotoInputRef.current) {
      mainPhotoInputRef.current.value = "";
    }
  };

  const clearReferencePhoto = () => {
    setReferencePhoto(null);
    setReferencePhotoPreview(null);
    if (referencePhotoInputRef.current) {
      referencePhotoInputRef.current.value = "";
    }
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!mainPhoto) {
        throw new Error("Bitte laden Sie ein Hauptfoto hoch");
      }

      const compressionOptions = { maxWidth: 1920, maxHeight: 1920, quality: 0.8, maxSizeMB: 1 };
      
      const compressedMainPhoto = await compressImageToFile(mainPhoto, compressionOptions);
      
      let compressedReferencePhoto: File | null = null;
      if (referencePhoto) {
        compressedReferencePhoto = await compressImageToFile(referencePhoto, compressionOptions);
      }

      const formData = new FormData();
      formData.append("mainPhoto", compressedMainPhoto);
      formData.append("mode", mode);
      if (compressedReferencePhoto) {
        formData.append("referencePhoto", compressedReferencePhoto);
      }
      if (message.trim()) {
        formData.append("message", message.trim());
      }

      const response = await fetch("/api/visual-brand/request", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Anfrage fehlgeschlagen");
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Anfrage gesendet",
        description: "Ihre Brand-Visuals werden erstellt. Wir leiten Sie zur Galerie weiter.",
      });
      // Invalidate both queryKey variants so BrandVisuals page polls immediately
      queryClient.invalidateQueries({ queryKey: ["/api/visual-brand-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/visual-brand/requests"] });
      resetForm();
      onOpenChange(false);
      // Navigate to Brand Visuals page so the user sees the spinning status card
      setLocation("/brand-visuals");
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setMode("person");
    setMainPhoto(null);
    setMainPhotoPreview(null);
    setReferencePhoto(null);
    setReferencePhotoPreview(null);
    setMessage("");
  };

  const handleClose = () => {
    if (!submitMutation.isPending) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-500" />
            Visual Brand Creator
          </DialogTitle>
          <DialogDescription>
            Laden Sie ein Foto hoch, und wir erstellen daraus 3 professionelle Brand-Visuals.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <Label className="text-base font-medium">Modus auswählen</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as "person" | "product")}
              className="grid grid-cols-2 gap-4"
            >
              <Label
                htmlFor="mode-person"
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  mode === "person"
                    ? "border-purple-500 bg-purple-50 dark:bg-purple-950/20"
                    : "border-border hover-elevate"
                }`}
              >
                <RadioGroupItem value="person" id="mode-person" className="sr-only" />
                <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="font-medium">Person</div>
                  <div className="text-xs text-muted-foreground">Portraits & Personal Branding</div>
                </div>
              </Label>
              
              <Label
                htmlFor="mode-product"
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  mode === "product"
                    ? "border-purple-500 bg-purple-50 dark:bg-purple-950/20"
                    : "border-border hover-elevate"
                }`}
              >
                <RadioGroupItem value="product" id="mode-product" className="sr-only" />
                <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="font-medium">Produkt</div>
                  <div className="text-xs text-muted-foreground">Produktfotos & Marketing</div>
                </div>
              </Label>
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label className="text-base font-medium">
              Hauptfoto hochladen <span className="text-red-500">*</span>
            </Label>
            <input
              ref={mainPhotoInputRef}
              type="file"
              accept="image/*"
              onChange={handleMainPhotoSelect}
              className="hidden"
              data-testid="input-main-photo"
            />
            
            {mainPhotoPreview ? (
              <Card className="relative overflow-hidden">
                <img
                  src={mainPhotoPreview}
                  alt="Hauptfoto Vorschau"
                  className="w-full h-48 object-cover"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={clearMainPhoto}
                  data-testid="button-clear-main-photo"
                >
                  <X className="w-4 h-4" />
                </Button>
              </Card>
            ) : (
              <div
                onClick={() => mainPhotoInputRef.current?.click()}
                className="border-2 border-dashed border-purple-300 dark:border-purple-700 rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 transition-colors"
                data-testid="dropzone-main-photo"
              >
                <Upload className="w-10 h-10 mx-auto text-purple-400 mb-3" />
                <p className="text-sm text-muted-foreground">
                  Hier klicken oder ein Bild hierher ziehen
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG, WEBP (max. 10MB)
                </p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Label className="text-base font-medium">
              Referenzbild (optional)
            </Label>
            <p className="text-sm text-muted-foreground">
              Laden Sie ein Stil-Referenzbild hoch, an dem sich die KI orientieren soll.
            </p>
            <input
              ref={referencePhotoInputRef}
              type="file"
              accept="image/*"
              onChange={handleReferencePhotoSelect}
              className="hidden"
              data-testid="input-reference-photo"
            />
            
            {referencePhotoPreview ? (
              <Card className="relative overflow-hidden">
                <img
                  src={referencePhotoPreview}
                  alt="Referenzbild Vorschau"
                  className="w-full h-32 object-cover"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={clearReferencePhoto}
                  data-testid="button-clear-reference-photo"
                >
                  <X className="w-4 h-4" />
                </Button>
              </Card>
            ) : (
              <div
                onClick={() => referencePhotoInputRef.current?.click()}
                className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-4 text-center cursor-pointer hover:border-purple-400 transition-colors"
                data-testid="dropzone-reference-photo"
              >
                <ImagePlus className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground">
                  Optional: Stil-Referenz hochladen
                </p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Label htmlFor="message" className="text-base font-medium">
              Nachricht an die KI (optional)
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="z.B. Warme Farben, moderner Look, minimalistisch..."
              className="resize-none"
              rows={3}
              data-testid="textarea-message"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitMutation.isPending}
            data-testid="button-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={!mainPhoto || submitMutation.isPending}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="button-submit-brand-request"
          >
            {submitMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Wird erstellt...
              </>
            ) : (
              <>
                <Palette className="w-4 h-4 mr-2" />
                Brand Visuals erstellen
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
